/**
 * Ownership stamp for the cached `behtari_persona` localStorage value.
 *
 * WHY THIS EXISTS
 * ---------------
 * `useAuthState.maybeSwapSocialSession()` has a fast path: if `behtari_persona`
 * is already in localStorage, the user is treated as an established, confirmed
 * OAuth user and the CNIC session-swap is skipped without a profile round-trip.
 *
 * That shortcut was unscoped — it could not tell "this device already finalized
 * THIS account" from "this device has a leftover persona from a DIFFERENT
 * account signed in earlier". A leftover value silently suppressed the swap for
 * a genuinely un-swapped OAuth session.
 *
 * `finalizeOAuthSession` — the only writer whose value the fast path is actually
 * reasoning about — now stamps the owning auth user id alongside the persona.
 * The fast path requires that stamp to match the CURRENT session's user before
 * short-circuiting, so it keeps its original benefit (no network round-trip for
 * a user we already finalized) while a cross-account leftover can no longer
 * block the swap.
 *
 * A missing/mismatched stamp is never fatal: it only means "don't take the
 * shortcut", and the normal profile-based path runs instead.
 */

const KEY = 'behtari_persona_uid';

/** Stamp the current cached persona as belonging to `userId`. */
export function rememberPersonaOwner(userId: string): void {
  try { localStorage.setItem(KEY, userId); } catch { /* no-op */ }
}

/** True only when a persona was cached by the finalizer FOR this exact user. */
export function personaBelongsTo(userId: string): boolean {
  try { return localStorage.getItem(KEY) === userId; } catch { return false; }
}

export function clearPersonaOwner(): void {
  try { localStorage.removeItem(KEY); } catch { /* no-op */ }
}
